import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Link as LinkIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

export interface PatientChatLinkTarget {
  id: string;
  name?: string | null;
}

interface ChatCandidate {
  contact_id: string;
  contact_name: string;
  contact_phone: string | null;
  avatar_url: string | null;
  conversation_id: string;
  channel_name: string | null;
  conversation_status: string | null;
  last_message_at: string | null;
}

interface Props {
  /** Paciente recém-salvo; `null` mantém o componente inativo. */
  patient: PatientChatLinkTarget | null;
  onDone: () => void;
  onLinked?: (info: { patientId: string; contactId: string; contactName: string }) => void;
}

function formatLastMessage(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Depois de salvar um paciente sem chat vinculado, procura conversas com o
 * mesmo telefone (normalizado) e oferece o vínculo. "Não vincular" fica
 * registrado e a pergunta não volta para aquele par chat ↔ paciente.
 */
const PatientChatLinkPrompt: React.FC<Props> = ({ patient, onDone, onLinked }) => {
  const [open, setOpen] = useState(false);
  const [chats, setChats] = useState<ChatCandidate[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await axios.get(
          getApiUrl(`/clinic/healthcare-panel/patient/${patient.id}/chat-link-suggestion`),
        );
        const list = (res?.success && !res.data?.already_linked ? res.data?.chats || [] : []) as ChatCandidate[];
        if (cancelled) return;
        if (list.length === 0) {
          onDoneRef.current();
          return;
        }
        setChats(list);
        setSelectedId(list[0].contact_id);
        setError('');
        setOpen(true);
      } catch {
        if (!cancelled) onDoneRef.current();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  const finish = () => {
    setOpen(false);
    setChats([]);
    onDone();
  };

  const dismiss = async () => {
    const ids = chats.map((c) => c.contact_id);
    const pid = patient?.id;
    finish();
    if (!pid || ids.length === 0) return;
    try {
      await axios.post(getApiUrl(`/clinic/healthcare-panel/patient/${pid}/chat-link-suggestion/dismiss`), {
        contact_ids: ids,
      });
    } catch {
      /* recusa não crítica */
    }
  };

  const link = async () => {
    const chat = chats.find((c) => c.contact_id === selectedId);
    if (!patient?.id || !chat) return;
    setBusy(true);
    setError('');
    try {
      const { data: res } = await axios.post(getApiUrl('/clinic/healthcare-panel/link'), {
        contact_id: chat.contact_id,
        patient_id: patient.id,
      });
      if (!res?.success) throw new Error(res?.error || 'Falha ao vincular');
      onLinked?.({ patientId: patient.id, contactId: chat.contact_id, contactName: chat.contact_name });
      finish();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Não foi possível vincular o chat.');
    } finally {
      setBusy(false);
    }
  };

  const single = chats.length === 1;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (busy || reason === 'backdropClick') return;
        void dismiss();
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <WhatsAppIcon sx={{ color: '#25D366' }} />
        Vincular a um chat existente?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {single
            ? `Já existe uma conversa com o mesmo telefone de ${patient?.name || 'este paciente'}.`
            : `Encontramos ${chats.length} conversas com o mesmo telefone de ${patient?.name || 'este paciente'}. Escolha qual vincular.`}
        </Typography>

        <RadioGroup value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
          {chats.map((c) => {
            const last = formatLastMessage(c.last_message_at);
            const details = [c.contact_phone, c.channel_name, last ? `última msg. ${last}` : null]
              .filter(Boolean)
              .join(' · ');
            return (
              <FormControlLabel
                key={c.contact_id}
                value={c.contact_id}
                control={<Radio size="small" sx={single ? { display: 'none' } : undefined} />}
                sx={{
                  mx: 0,
                  mb: 1,
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: selectedId === c.contact_id ? 'primary.main' : 'divider',
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Avatar src={c.avatar_url || undefined} sx={{ width: 34, height: 34 }}>
                      {(c.contact_name || 'C').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {c.contact_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {details || 'Conversa no inbox'}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            );
          })}
        </RadioGroup>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Com o vínculo, o histórico e os dados do paciente aparecem direto no chat. Se escolher “Não vincular”, esta
          pergunta não aparece de novo para essa conversa.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => void dismiss()} disabled={busy} sx={{ textTransform: 'none' }}>
          Não vincular
        </Button>
        <Button
          variant="contained"
          onClick={() => void link()}
          disabled={busy || !selectedId}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
          sx={{ textTransform: 'none' }}
        >
          Vincular
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientChatLinkPrompt;
